// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Il modello ISA, lato interfaccia: chi può fare cosa, come si scrivono e si
 * leggono i valori dei righi, e la copia dei valori calcolati.
 *
 * I conti e il confronto li fa il server (src/domain/isa/*): qui i valori
 * arrivano come stringhe decimali esatte e si mandano indietro nella forma che
 * l'API accetta, senza passare per un numero in virgola mobile.
 */
import { formatMoney } from './bilancio.js';

// ── Ruoli ─────────────────────────────────────────────────────────────

/**
 * Copia speculare delle costanti ISA di src/api/bilancio-roles.js:
 * test/frontend-isa.test.js le tiene allineate. Il server resta l'autorità.
 */
export const ISA_READ_ROLES = Object.freeze(['admin', 'operator', 'readonly', 'commercialista']);
export const ISA_WRITE_ROLES = Object.freeze(['admin', 'operator']);
export const ISA_CONFIRM_ROLES = Object.freeze(['admin', 'operator']);
export const ISA_DELETE_ROLES = Object.freeze(['admin']);

/** Cosa può fare con il modello ISA chi ha questo ruolo nell'azienda attiva. */
export function isaPermissions(role) {
  return {
    read: ISA_READ_ROLES.includes(role),
    write: ISA_WRITE_ROLES.includes(role),
    confirm: ISA_CONFIRM_ROLES.includes(role),
    remove: ISA_DELETE_ROLES.includes(role),
  };
}

// ── Periodi ───────────────────────────────────────────────────────────

/**
 * I periodi d'imposta per cui esiste un modello ISA: dal 2018, quando gli ISA
 * hanno sostituito gli studi di settore, all'ultimo periodo chiuso, l'anno
 * prima di quello in corso a Roma. Il modello prende il nome dall'anno in cui
 * si presenta, uno dopo il periodo: «ISA 2026» è il periodo d'imposta 2025.
 * È la stessa regola del server (src/domain/isa/periodo.js); `now` si passa
 * per provarla con un orologio fisso.
 */
export const PRIMO_PERIODO_ISA = 2018;

/** L'anno del modello di un periodo d'imposta: quello in cui si presenta. */
export const annoModelloOf = (periodo) => periodo + 1;

/** L'anno in corso a Roma in quell'istante. */
export function annoCorrenteRoma(now = new Date()) {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', year: 'numeric' }).format(now));
}

/** Il primo e l'ultimo periodo ISA, con l'anno dei loro modelli. */
export function periodiIsa(now = new Date()) {
  const ultimo = annoCorrenteRoma(now) - 1;
  return { primo: PRIMO_PERIODO_ISA, ultimo, primoModello: annoModelloOf(PRIMO_PERIODO_ISA), ultimoModello: annoModelloOf(ultimo) };
}

/** `ammesso`, `prima_degli_isa` (valevano gli studi di settore) o `non_chiuso` (il periodo non è ancora finito). */
export function periodoIsaState(periodo, now = new Date()) {
  const { primo, ultimo } = periodiIsa(now);
  if (!Number.isInteger(periodo) || periodo < primo) return 'prima_degli_isa';
  return periodo > ultimo ? 'non_chiuso' : 'ammesso';
}

/** Se per il periodo esiste un modello ISA. */
export const isPeriodoIsa = (periodo, now = new Date()) => periodoIsaState(periodo, now) === 'ammesso';

/**
 * Se una scheda è di sola lettura: quella di un periodo senza modello ISA
 * (prima del 2018 o non ancora chiuso) si consulta, si confronta, si scarica e
 * si elimina, ma non si modifica; il server rifiuta ogni modifica.
 */
export const recordReadOnly = (record, now = new Date()) => Boolean(record) && !isPeriodoIsa(record.periodoImposta, now);

// ── Modelli ───────────────────────────────────────────────────────────

/**
 * L'elenco dei modelli di un anno (`GET …/isa/modelli`) raggruppato per
 * settore, per una scelta con un gruppo per settore: settori e modelli in
 * ordine alfabetico.
 *
 * @param {{ codiceIsa: string, settore: string }[]} modelli
 * @returns {{ settore: string, modelli: object[] }[]}
 */
export function modelsBySector(modelli) {
  const groups = new Map();
  for (const model of modelli ?? []) {
    if (!groups.has(model.settore)) groups.set(model.settore, []);
    groups.get(model.settore).push(model);
  }
  return [...groups.keys()].sort().map((settore) => ({
    settore,
    modelli: groups.get(settore).sort((a, b) => a.codiceIsa.localeCompare(b.codiceIsa)),
  }));
}

/** I valori ammessi come li si confronta tra due cataloghi: senza spazi. */
const admissible = (valori) => String(valori ?? '').replace(/\s+/g, '');

/** Una descrizione come la si confronta tra due cataloghi: senza maiuscole, virgolette, spazi doppi e punteggiatura. */
const wording = (text) => String(text ?? '').normalize('NFKC').toLowerCase()
  .replace(/[’‘`´]/g, "'")
  .replace(/[^\p{L}\p{N}%']+/gu, ' ')
  .trim();

/**
 * Se un campo vuol dire la stessa cosa in due cataloghi: stesso codice,
 * quadro, unità, descrizione e valori ammessi. Il codice da solo non basta: C01
 * è il software in DG66U e «Audit EDP» in DK27U; B00 conta fino a 20 unità
 * locali in DG66U e 10 in DK27U. È la stessa regola del server
 * (src/domain/isa/catalog.js, sameField).
 */
export function sameField(a, b) {
  return Boolean(a && b) && a.code === b.code && a.quadro === b.quadro && a.unita === b.unita
    && wording(a.descrizione) === wording(b.descrizione) && admissible(a.valori) === admissible(b.valori);
}

/** I quadri comuni a tutti i modelli: i loro righi vogliono dire la stessa cosa da un modello all'altro. */
const SHARED_QUADRI = new Set(['frontespizio', 'A', 'F', 'H']);

/**
 * Se un valore salvato passa a un altro catalogo (periodo, modello o
 * tipologia): solo se il campo è lo stesso nei due (sameField) e o è un rigo
 * dei quadri comuni — frontespizio, A, F, H — o si resta sullo stesso modello e
 * anno (cambiando la sola tipologia restano i righi del modello). I righi
 * propri di un modello (B, C, D, E, i dati dell'Agenzia) non passano mai a un
 * altro modello o anno, per quanto simile sia la descrizione: C07 è «Altro» sia
 * in DG13U sia in DG81U, di attività diverse. È la stessa regola del server
 * (src/domain/isa/catalog.js, carriesOver).
 */
export function carriesOver(a, b, sameModel) {
  if (!sameField(a, b)) return false;
  return sameModel ? a.quadro !== 'agenzia' : SHARED_QUADRI.has(a.quadro);
}

/**
 * I valori dichiarati che restano passando a un altro modello, tipologia o
 * anno: solo quelli che carriesOver() lascia passare e che si inseriscono a
 * mano. Gli altri si tolgono, con la descrizione che avevano, per mostrarli
 * prima di confermare.
 *
 * @param {Record<string, string|boolean>} valori
 * @param {{ fields: Map<string, object>|object[], codiceIsa: string, anno: number }} current - il modello attuale e i suoi campi (i righi del confronto)
 * @param {{ codiceIsa: string, anno: number, quadri: { campi: object[] }[] }} modello - `GET …/isa/modelli/{codiceIsa}`
 * @returns {{ valori: Record<string, string|boolean>, tolti: { code: string, descrizione: string }[] }}
 */
export function valuesForModel(valori, current, modello) {
  const fields = current?.fields ?? [];
  const before = fields instanceof Map ? fields : new Map(fields.map((field) => [field.code, field]));
  const after = new Map((modello?.quadri ?? []).flatMap((quadro) => quadro.campi).filter((field) => field.inseribile).map((field) => [field.code, field]));
  const sameModel = current?.codiceIsa === modello?.codiceIsa && current?.anno === modello?.anno;
  const kept = {};
  const tolti = [];
  for (const [code, value] of Object.entries(valori ?? {})) {
    if (carriesOver(before.get(code), after.get(code), sameModel)) kept[code] = value;
    else tolti.push({ code, descrizione: before.get(code)?.descrizione ?? code });
  }
  return { valori: kept, tolti };
}

/** Se chiedere la tipologia di reddito: il codice ATECO porta a due modelli diversi, oppure manca. */
export function asksTipologia(modello) {
  const per = modello?.perTipologia ?? {};
  if (!modello?.ateco) return true;
  return Boolean(per.impresa && per.lavoro_autonomo && per.impresa !== per.lavoro_autonomo);
}

// ── Esiti ─────────────────────────────────────────────────────────────

/**
 * Il colore del badge di ogni esito. Il colore non è mai l'unico segnale: il
 * badge porta sempre l'esito scritto.
 */
export const ISA_ESITO_BADGE = Object.freeze({
  coincide: 'badge-green',
  vicino: 'badge-blue',
  diverso: 'badge-yellow',
  non_verificabile: 'badge-gray',
  solo_dichiarato: 'badge-gray',
  non_dichiarato: 'badge-gray',
  non_catalogato: 'badge-gray',
});

/**
 * I quadri aperti all'inizio: l'identità del modello e i dati contabili, del
 * quadro F per un'impresa o H per un professionista (un catalogo ha l'uno o
 * l'altro). Gli altri si compilano soprattutto a mano; i dati dell'Agenzia
 * restano chiusi.
 */
export const OPEN_QUADRI = Object.freeze(['frontespizio', 'F', 'H']);

/** Gli esiti che il filtro «solo differenze» tiene. */
export const DIFFERENCE_ESITI = Object.freeze(['diverso', 'vicino']);

/**
 * I righi di un quadro da mostrare: tutti, oppure solo quelli diversi o vicini.
 *
 * @param {object[]} righe
 * @param {boolean} onlyDifferences
 */
export function visibleRows(righe, onlyDifferences) {
  if (!onlyDifferences) return righe ?? [];
  return (righe ?? []).filter((row) => DIFFERENCE_ESITI.includes(row.esito));
}

/** Quanti valori dichiarati di un quadro non hanno un rigo nel catalogo. */
export const uncataloguedCount = (righe) => (righe ?? []).filter((row) => row.esito === 'non_catalogato').length;

/**
 * Se un quadro si apre: quelli di OPEN_QUADRI, tutti con il filtro «solo
 * differenze», e ogni quadro con valori dichiarati senza rigo nel catalogo,
 * che chiuso li nasconderebbe.
 */
export const isQuadroOpen = (quadro, righe, onlyDifferences) =>
  onlyDifferences || OPEN_QUADRI.includes(quadro) || uncataloguedCount(righe) > 0;

/**
 * L'etichetta della colonna «Rigo»: il rigo con il campo («A06 c.2»); per un
 * valore del frontespizio senza rigo nel catalogo, il suo codice, perché il
 * frontespizio ha campi, non righi.
 */
export function rigoLabel(row) {
  if (row.rigo) return row.campo ? `${row.rigo} c.${row.campo}` : row.rigo;
  return row.catalogato === false ? row.code : '';
}

// ── Valori ────────────────────────────────────────────────────────────

const MONEY = /^-?\d{1,13}([.,]\d{1,2})?$/;
const DECIMAL = /^-?\d{1,15}(\.\d{1,6})?$/;

/** Le cifre di un valore codificato: CBNn nelle specifiche vale n cifre («001» per CBN3). */
export function codeWidth(field) {
  const match = /^CBN(\d{1,2})$/.exec(field?.formato ?? '');
  return match ? Number(match[1]) : 2;
}

/**
 * Un valore codificato (`lista`) come lo vuole il file: da una a n cifre,
 * riscritto con gli zeri davanti fino a n, e tra i `valori` quando le
 * specifiche li elencano; null se non lo è. È la stessa regola del server
 * (src/domain/isa/compare.js).
 */
export function codedValue(field, text) {
  const width = codeWidth(field);
  if (!new RegExp(`^\\d{1,${width}}$`).test(text)) return null;
  if (!withinValori(field.valori, Number(text))) return null;
  return String(Number(text)).padStart(width, '0');
}

/**
 * I limiti che i `valori` delle specifiche indicano: «1-20», «0, 1, 2» o un
 * misto, come coppie `[minimo, massimo]`; null se non ne indicano o non si
 * leggono. Un intervallo resta i suoi due estremi, mai elencato, e la lista ha
 * un tetto. È la stessa regola del server (src/domain/isa/compare.js).
 */
export function valoriRanges(valori) {
  if (typeof valori !== 'string' || !valori.trim() || valori.length > 200) return null;
  const parts = valori.split(',').map((part) => part.trim());
  if (parts.length > 50) return null;
  const ranges = [];
  for (const part of parts) {
    const match = /^(\d{1,9})(?:\s*-\s*(\d{1,9}))?$/.exec(part);
    if (!match) return null;
    const low = Number(match[1]);
    const high = match[2] === undefined ? low : Number(match[2]);
    if (high < low) return null;
    ranges.push([low, high]);
  }
  return ranges;
}

/**
 * Se un numero è tra quelli che i `valori` ammettono: qualsiasi numero se le
 * specifiche non ne indicano; nessuno se ne indicano di illeggibili (un limite
 * che non si legge non vale come assenza di limiti).
 */
export function withinValori(valori, number) {
  if (valori === undefined || valori === null || (typeof valori === 'string' && !valori.trim())) return true;
  const ranges = valoriRanges(valori);
  return Boolean(ranges) && ranges.some(([low, high]) => number >= low && number <= high);
}

/**
 * Un valore scritto dall'utente nella forma che l'API accetta, o il motivo
 * per cui non lo è. Gli importi accettano la virgola o il punto per i decimali
 * e i separatori delle migliaia scritti con il punto («1.234,50»).
 *
 * @param {{ unita: string, negativo?: boolean, valori?: string }} field
 * @param {string|boolean|null|undefined} input
 * @returns {{ ok: true, value: string|boolean|null } | { ok: false, reason: string }}
 */
export function parseIsaInput(field, input) {
  const parsed = parseUnit(field, input);
  // Un numero che le specifiche limitano («1-10» unità locali, «0-24» mesi) resta nei limiti.
  if (parsed.ok && parsed.value !== null && BOUNDED_UNITS.has(field.unita) && !withinValori(field.valori, Number(parsed.value))) {
    return { ok: false, reason: 'intervallo' };
  }
  return parsed;
}

/** Le unità il cui valore è un numero, e quindi limitato dai `valori` quando le specifiche li danno. */
const BOUNDED_UNITS = new Set(['euro', 'percentuale', 'aliquota', 'numero', 'decimale']);

/** Un valore scritto, letto per la sua unità, prima dei limiti. */
function parseUnit(field, input) {
  if (field.unita === 'casella') return { ok: true, value: Boolean(input) };
  const text = String(input ?? '').trim();
  if (text === '') return { ok: true, value: null };
  switch (field.unita) {
    case 'euro': {
      // «1.234,50»: dots are thousands separators when a comma is present, or when grouped by three.
      let normalised = text.replace(/\s/g, '').replace(/€/g, '');
      if (normalised.includes(',')) normalised = normalised.replace(/\./g, '').replace(',', '.');
      else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalised)) normalised = normalised.replace(/\./g, '');
      if (!MONEY.test(normalised)) return { ok: false, reason: 'euro' };
      if (normalised.startsWith('-') && !field.negativo) return { ok: false, reason: 'negativo' };
      return { ok: true, value: normalised };
    }
    case 'percentuale': {
      const value = text.replace(/\s|%/g, '');
      if (!/^\d{1,3}$/.test(value) || Number(value) > 100) return { ok: false, reason: 'percentuale' };
      return { ok: true, value: String(Number(value)) };
    }
    case 'aliquota': {
      const value = text.replace(/\s|%/g, '').replace(',', '.');
      if (!/^\d{1,3}(\.\d{1,2})?$/.test(value) || Number(value) > 100) return { ok: false, reason: 'aliquota' };
      return { ok: true, value };
    }
    case 'numero': {
      if (!/^\d{1,9}$/.test(text)) return { ok: false, reason: 'numero' };
      return { ok: true, value: String(Number(text)) };
    }
    case 'decimale': {
      const value = text.replace(/\s/g, '').replace(',', '.');
      if (!DECIMAL.test(value)) return { ok: false, reason: 'decimale' };
      if (value.startsWith('-') && !field.negativo) return { ok: false, reason: 'negativo' };
      return { ok: true, value };
    }
    case 'lista': {
      const coded = codedValue(field, text);
      return coded === null ? { ok: false, reason: 'lista' } : { ok: true, value: coded };
    }
    case 'provincia':
      return /^[A-Za-z]{2}$/.test(text) ? { ok: true, value: text.toUpperCase() } : { ok: false, reason: 'provincia' };
    case 'codice_attivita':
      return /^\d{6}$/.test(text) ? { ok: true, value: text } : { ok: false, reason: 'codice_attivita' };
    case 'codice_fiscale':
      return /^[A-Za-z0-9]{11,16}$/.test(text) ? { ok: true, value: text.toUpperCase() } : { ok: false, reason: 'codice_fiscale' };
    default:
      return text.length <= 100 ? { ok: true, value: text } : { ok: false, reason: 'testo' };
  }
}

/** Un valore dell'API come lo si mostra in un campo da modificare: la virgola per i decimali. */
export function toInputText(field, value) {
  if (value === null || value === undefined) return '';
  if (field.unita === 'casella') return value === true || value === 'true';
  if (['euro', 'aliquota', 'decimale'].includes(field.unita)) return String(value).replace('.', ',');
  return String(value);
}

/**
 * Un valore (calcolato o dichiarato) da leggere: gli importi in euro, le
 * percentuali con il simbolo, le caselle come Sì/No.
 *
 * @param {{ unita: string }} row
 * @param {string|null|undefined} value
 * @param {{ yes: string, no: string }} words
 */
export function formatIsaValue(row, value, words = { yes: 'Sì', no: 'No' }) {
  if (value === null || value === undefined || value === '') return '—';
  switch (row.unita) {
    case 'euro': return formatMoney(value) ?? String(value);
    case 'percentuale': return `${value} %`;
    case 'aliquota': return `${String(value).replace('.', ',')} %`;
    case 'decimale': return String(value).replace('.', ',');
    case 'casella': return value === true || value === 'true' ? words.yes : words.no;
    default: return String(value);
  }
}

/** Arrotonda un importo esatto all'euro, come lo vuole il modello («,00»), senza passare per un float. */
export function wholeEuros(value) {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value ?? ''));
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  let units = BigInt(whole);
  if (fraction && Number(fraction[0]) >= 5) units += 1n;
  return units === 0n ? '0' : `${sign}${units}`;
}

/**
 * Riempie i campi ancora vuoti con i valori calcolati: solo i righi inseribili
 * con un valore ricavato o stimato (D, S). Un valore «indicativo», che poggia
 * su fatture la cui copertura non è confermata, non si copia: si scrive a mano.
 *
 * @param {object[]} righe - i righi del confronto
 * @param {Record<string, string|boolean>} draft - i valori in corso di modifica, nella forma del campo
 * @returns {{ draft: Record<string, string|boolean>, copied: number }}
 */
export function copyCalculated(righe, draft) {
  const next = { ...draft };
  let copied = 0;
  for (const row of righe) {
    if (!row.inseribile || !['D', 'S'].includes(row.statoEffettivo)) continue;
    if (row.valore === null || row.valore === undefined) continue;
    const current = next[row.code];
    if (row.unita === 'casella') {
      if (current === undefined) { next[row.code] = row.valore === 'true'; copied += 1; }
      continue;
    }
    if (current !== undefined && current !== '') continue;
    const value = row.unita === 'euro' ? wholeEuros(row.valore) : row.valore;
    if (value === null) continue;
    next[row.code] = toInputText(row, value);
    copied += 1;
  }
  return { draft: next, copied };
}

/**
 * I valori da mandare all'API, o il primo campo che non va.
 *
 * @param {Map<string, object>|object[]} fields - i campi inseribili
 * @param {Record<string, string|boolean>} draft
 * @returns {{ ok: true, valori: Record<string, string|boolean> } | { ok: false, code: string, reason: string }}
 */
export function declaredPayload(fields, draft) {
  const byCode = fields instanceof Map ? fields : new Map(fields.map((field) => [field.code, field]));
  const valori = {};
  for (const [code, raw] of Object.entries(draft)) {
    const field = byCode.get(code);
    if (!field || !field.inseribile) continue;
    const parsed = parseIsaInput(field, raw);
    if (!parsed.ok) return { ok: false, code, reason: parsed.reason };
    if (field.unita === 'casella') {
      if (parsed.value) valori[code] = true;
      continue;
    }
    if (parsed.value !== null) valori[code] = parsed.value;
  }
  return { ok: true, valori };
}

/** I valori dichiarati salvati come bozza da modificare. */
export function draftFromDeclared(fields, valori) {
  const byCode = fields instanceof Map ? fields : new Map(fields.map((field) => [field.code, field]));
  const draft = {};
  for (const [code, value] of Object.entries(valori ?? {})) {
    const field = byCode.get(code);
    if (field) draft[code] = toInputText(field, value);
  }
  return draft;
}

/** Il VE50 salvato nella scheda come lo mostra il campo, con la virgola; vuoto se non c'è. */
export function ve50Text(record) {
  return toInputText({ unita: 'euro' }, record?.volumeAffariDichiarazioneIva) || '';
}

/**
 * Il VE50 da mostrare quando la scheda si ricarica (dopo il salvataggio dei
 * righi, la conferma della copertura, il collegamento del bilancio): quello
 * che l'utente sta scrivendo resta finché è diverso dal salvato, e resta
 * quindi «non salvato». Si riparte dal valore salvato solo dopo il suo stesso
 * salvataggio (`reset`), per un'altra scheda o al primo caricamento.
 *
 * @param {{ input: string, previous: object|null, next: object, reset?: boolean }} args
 * @returns {string}
 */
export function ve50AfterReload({ input, previous, next, reset = false }) {
  const saved = ve50Text(next);
  if (reset || !previous || previous.id !== next?.id) return saved;
  return input !== ve50Text(previous) ? input : saved;
}

/**
 * Se ci sono modifiche non salvate che un cambio di modello perderebbe: i valori
 * dichiarati in corso di scrittura (`dirty`) o un VE50 diverso da quello della
 * scheda. Il salvataggio dei soli righi lascia il VE50 in sospeso, e il cambio
 * resta bloccato finché anche quello non è salvato o scartato.
 *
 * @param {{ dirty: boolean, ve50Input: string, record: object|null }} state
 */
export function hasUnsavedEdits({ dirty, ve50Input, record }) {
  return Boolean(dirty) || (Boolean(record) && ve50Input !== ve50Text(record));
}

/** Il volume d'affari della dichiarazione IVA nella forma dell'API, o null se vuoto; undefined se non valido. */
export function parseVe50(input) {
  const parsed = parseIsaInput({ unita: 'euro', negativo: false }, input);
  return parsed.ok ? parsed.value : undefined;
}
